/**
 * Feature 9 — Admin dispute queue + resolution view.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { disputesApi, type DisputeOutcome } from '../../services/disputes';
import type { DisputeAdminListItem } from '../../types';


const OUTCOMES: { value: DisputeOutcome; label: string }[] = [
  { value: 'upheld', label: 'Score upheld' },
  { value: 'corrected', label: 'Score corrected' },
  { value: 'confidence_adjusted', label: 'Confidence adjusted' },
  { value: 'data_excluded', label: 'Data excluded' },
  { value: 'more_info', label: 'More info requested' },
  { value: 'dismissed', label: 'Dismissed' },
];


function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    under_review: 'bg-amber-50 text-amber-700 border-amber-200',
    more_info_requested: 'bg-slate-100 text-slate-700 border-slate-200',
    resolved_upheld: 'bg-slate-100 text-slate-700 border-slate-200',
    resolved_corrected: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    resolved_confidence_adjusted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    resolved_data_excluded: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dismissed: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}


function DisputeModal({
  disputeId, onClose,
}: { disputeId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admin', 'disputes', disputeId],
    queryFn: () => disputesApi.adminGet(disputeId),
  });
  const [outcome, setOutcome] = useState<DisputeOutcome>('upheld');
  const [notes, setNotes] = useState('');

  const action = useMutation({
    mutationFn: () => disputesApi.adminResolve(disputeId, { outcome, notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'disputes'] });
      onClose();
    },
  });

  const needsNotes = outcome === 'more_info';
  const disabled = action.isPending || (needsNotes && !notes.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {!data ? (
          <div className="p-12 flex justify-center"><LoadingSpinner size="md" /></div>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-slate-900">Dispute</h2>
            <p className="mt-1 text-xs text-slate-500">
              {data.reason_display}
              {' · '}
              <span className={data.severity === 'critical' ? 'text-red-600' : 'text-slate-600'}>{data.severity}</span>
              {' · '}
              <StatusBadge status={data.status} />
            </p>

            <dl className="mt-4 space-y-3 text-sm">
              <div>
                <dt className="text-xs text-slate-400">Submitted by</dt>
                <dd className="text-slate-800">{data.submitted_by_email || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-slate-400">Justification</dt>
                <dd className="text-slate-700 whitespace-pre-wrap">{data.justification}</dd>
              </div>
              {data.related_score_snapshot && (
                <div><dt className="text-xs text-slate-400">Related score snapshot</dt><dd className="text-slate-700 font-mono text-xs">{data.related_score_snapshot}</dd></div>
              )}
              {data.related_upload && (
                <div><dt className="text-xs text-slate-400">Related upload</dt><dd className="text-slate-700 font-mono text-xs">{data.related_upload}</dd></div>
              )}
              {data.related_parsed_event && (
                <div><dt className="text-xs text-slate-400">Related parsed event</dt><dd className="text-slate-700 font-mono text-xs">{data.related_parsed_event}</dd></div>
              )}
              {data.related_rule_trigger && (
                <div><dt className="text-xs text-slate-400">Related rule trigger</dt><dd className="text-slate-700 font-mono text-xs">{data.related_rule_trigger}</dd></div>
              )}
              {data.outcome_notes && (
                <div><dt className="text-xs text-slate-400">Previous notes</dt><dd className="text-slate-700 whitespace-pre-wrap">{data.outcome_notes}</dd></div>
              )}
            </dl>

            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-semibold text-slate-900 mb-2">Resolve</div>
              <label className="block mb-3">
                <span className="text-xs font-medium text-slate-600">Outcome</span>
                <select
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value as DisputeOutcome)}
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                >
                  {OUTCOMES.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Notes {needsNotes ? '(required)' : '(optional)'}</span>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                />
              </label>
              {action.isError && (
                <p className="mt-2 text-xs text-red-600">Action failed.</p>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg text-slate-700 hover:bg-slate-100">
                Close
              </button>
              <button
                onClick={() => action.mutate()}
                disabled={disabled}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-500 text-white disabled:opacity-50 hover:bg-brand-600"
              >
                {action.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


export default function AdminDisputes() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [severityFilter, setSeverityFilter] = useState('');
  const [active, setActive] = useState<DisputeAdminListItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'disputes', page, statusFilter, severityFilter],
    queryFn: () => disputesApi.adminList({
      page: String(page),
      status: statusFilter || undefined,
      severity: severityFilter || undefined,
    }),
  });

  const totalPages = data ? Math.ceil(data.count / 20) : 1;

  return (
    <>
      <PageHeader title="Disputes" description="Vendor-submitted disputes" />

      <div className="mb-4 flex gap-3">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="under_review">Under review</option>
          <option value="more_info_requested">More info requested</option>
          <option value="resolved_upheld">Resolved — upheld</option>
          <option value="resolved_corrected">Resolved — corrected</option>
          <option value="resolved_confidence_adjusted">Resolved — confidence adjusted</option>
          <option value="resolved_data_excluded">Resolved — data excluded</option>
          <option value="dismissed">Dismissed</option>
        </select>
        <select
          value={severityFilter}
          onChange={(e) => { setSeverityFilter(e.target.value); setPage(1); }}
          className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        >
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="standard">Standard</option>
        </select>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        {isLoading ? (
          <div className="p-12 flex justify-center"><LoadingSpinner size="md" /></div>
        ) : !data || data.results.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">No disputes.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Vendor</th>
                <th className="px-4 py-3 text-left font-medium">Reason</th>
                <th className="px-4 py-3 text-left font-medium">Severity</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((d) => (
                <tr
                  key={d.id}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => setActive(d)}
                >
                  <td className="px-4 py-3 text-sm text-slate-800">{d.vendor_org_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{d.reason_display}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${d.severity === 'critical' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                      {d.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {new Date(d.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2 text-sm">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50">Previous</button>
          <span className="text-slate-500">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50">Next</button>
        </div>
      )}

      {active && (
        <DisputeModal disputeId={active.id} onClose={() => setActive(null)} />
      )}
    </>
  );
}
